import React from "react";
import { Avatar, Box, Flex, Heading, HStack, Icon, IconButton, Spacer, Text } from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom";
import { htmlToPlainText } from "@lubbezposrednio-frontend/core";
import { FiAlertCircle, FiArrowRight, FiCalendar, FiCheckCircle, FiClock, FiDownload, FiPlayCircle, FiUserPlus, FiXCircle } from "react-icons/fi";

export type VotingTileOrganizer = {
  name: string;
  avatarUrl?: string | null;
  profileRef?: string | null;
};

export type VotingTileQuestionPreview = { content: string; options: string[]; more: number };

export type VotingTileProps = {
  id: string;
  title: string | null;
  status: string; // ongoing | planned | finished | draft
  startDate?: string | null;
  endDate?: string | null;
  organizer?: VotingTileOrganizer | null;
  voted?: boolean | null; // tylko „Biorę udział"
  protocolUrl?: string | null;
  votersCount?: number | null;
  votesCount?: number | null;
  questionsCount?: number | null;
  questionPreview?: VotingTileQuestionPreview | null;
  signupOpen?: boolean; // „Odkrywaj" — zapisy otwarte
  signupClosesAt?: string | null; // termin zamknięcia zapisów
  ctaLabel?: string;
};

type CtaStyle = { bg: string; color: string; hover: string; shadow?: string };
type StatusMeta = { label: string; color: string; bg: string; icon: React.ElementType; cta: string; ctaStyle: CtaStyle };
const FILLED = (bg: string, hover: string, shadow: string): CtaStyle => ({ bg, color: "white", hover, shadow });
const OUTLINE: CtaStyle = { bg: "rgba(67,0,152,0.06)", color: "primary", hover: "rgba(67,0,152,0.12)" };

const STATUS_META: Record<string, StatusMeta> = {
  ongoing: { label: "Trwa", color: "#1f9e18", bg: "rgba(47,206,37,0.12)", icon: FiPlayCircle, cta: "Głosuj", ctaStyle: FILLED("#1f9e18", "#197d13", "0 8px 18px rgba(31,158,24,0.32)") },
  planned: { label: "Zaplanowane", color: "#2563eb", bg: "rgba(37,99,235,0.1)", icon: FiClock, cta: "Szczegóły", ctaStyle: OUTLINE },
  finished: { label: "Zakończone", color: "#6b7280", bg: "rgba(107,114,128,0.12)", icon: FiCheckCircle, cta: "Wyniki", ctaStyle: FILLED("#430098", "#360079", "0 8px 18px rgba(67,0,152,0.30)") },
  draft: { label: "Szkic", color: "#6b7280", bg: "rgba(107,114,128,0.12)", icon: FiClock, cta: "Zobacz", ctaStyle: OUTLINE }
};

const formatDate = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("pl-PL", { day: "2-digit", month: "short", year: "numeric" });
};

export const VotingTile: React.FC<VotingTileProps> = ({
  id,
  title,
  status,
  startDate,
  endDate,
  organizer,
  voted,
  protocolUrl,
  votersCount,
  votesCount,
  questionsCount,
  questionPreview,
  signupOpen,
  signupClosesAt,
  ctaLabel
}) => {
  const navigate = useNavigate();
  const meta = STATUS_META[status] ?? STATUS_META.planned;
  const votedOngoing = status === "ongoing" && voted === true;
  const cta = ctaLabel ?? (signupOpen ? "Zapisz się" : votedOngoing ? "Szczegóły" : meta.cta);
  const ctaStyle: CtaStyle = signupOpen ? FILLED("#e51f44", "#c41a3a", "0 8px 18px rgba(229,31,68,0.30)") : votedOngoing ? OUTLINE : meta.ctaStyle;

  const orgAvatar = organizer?.avatarUrl ? `${process.env.REACT_APP_BACKEND_URL}${organizer.avatarUrl}` : undefined;
  const orgLink = organizer?.profileRef ? `/profil/${organizer.profileRef}` : undefined;
  const backendUrl = process.env.REACT_APP_BACKEND_URL ?? "";

  const voters = typeof votersCount === "number" ? votersCount : 0;
  const votes = typeof votesCount === "number" ? votesCount : 0;
  const showTurnout = (status === "ongoing" || status === "finished") && voters > 0;
  const pct = showTurnout ? Math.min(100, Math.round((votes / voters) * 100)) : 0;

  const options = (questionPreview?.options ?? []).filter(Boolean);
  const qText = typeof questionsCount === "number" && questionsCount > 0 ? (questionsCount === 1 ? "1 pytanie" : `${questionsCount} pytań`) : null;

  // Terminy: od zapisów (przy zapisach) po termin głosowania.
  const terms = (() => {
    if (status === "ongoing") return endDate ? `Głosowanie do ${formatDate(endDate)}` : "";
    if (status === "finished") return endDate ? `Zakończono ${formatDate(endDate)}` : "";
    if (status === "planned") {
      const parts: string[] = [];
      if (signupClosesAt) parts.push(`Zapisy do ${formatDate(signupClosesAt)}`);
      if (startDate) parts.push(`Głosowanie ${formatDate(startDate)}${endDate ? `–${formatDate(endDate)}` : ""}`);
      return parts.join(" · ");
    }
    return "";
  })();

  const hasContentBox = !!terms || !!questionPreview?.content || showTurnout || voters > 0 || !!qText;

  return (
    <Flex
      as={Link}
      to={`/glosowanie/${id}`}
      role="group"
      direction="column"
      bg="white"
      borderRadius="20px"
      border="1px solid rgba(67,0,152,0.09)"
      boxShadow="0 8px 24px rgba(67,0,152,0.07)"
      transition="all 0.2s"
      h="100%"
      _hover={{ transform: "translateY(-4px)", boxShadow: "0 18px 40px rgba(67,0,152,0.15)", borderColor: "rgba(67,0,152,0.18)" }}
    >
      <Flex direction="column" flex="1" p="18px" gap="12px">
        {/* Nagłówek jak u posta: organizator = autor + stan po prawej */}
        <HStack justify="space-between" align="center" gap="8px">
          <HStack
            spacing="10px"
            minW={0}
            flex="1"
            cursor={orgLink ? "pointer" : "default"}
            onClick={orgLink ? e => { e.preventDefault(); e.stopPropagation(); navigate(orgLink); } : undefined}
            sx={orgLink ? { "&:hover .org-name": { textDecoration: "underline" } } : undefined}
          >
            <Avatar size="sm" name={organizer?.name || "?"} src={orgAvatar} bg="rgba(67,0,152,0.10)" color="primary" />
            <Text className="org-name" fontSize="14px" fontWeight="700" color="primary" noOfLines={1} minW={0}>
              {organizer?.name || "Organizator"}
            </Text>
          </HStack>

          {typeof voted === "boolean" ? (
            (() => {
              const missed = !voted && status === "finished"; // zakończone bez Twojego głosu
              const badgeBg = voted ? "rgba(47,206,37,0.12)" : missed ? "rgba(107,114,128,0.12)" : "rgba(229,31,68,0.08)";
              const badgeColor = voted ? "#1f9e18" : missed ? "#6b7280" : "#c41a3a";
              const badgeIcon = voted ? FiCheckCircle : missed ? FiXCircle : FiAlertCircle;
              const badgeLabel = voted ? "Zagłosowano" : missed ? "Nie oddano" : "Do oddania";
              return (
                <HStack spacing="4px" bg={badgeBg} color={badgeColor} px="9px" py="3px" borderRadius="full" flexShrink={0}>
                  <Icon as={badgeIcon} boxSize="12px" />
                  <Text fontSize="11px" fontWeight="700">{badgeLabel}</Text>
                </HStack>
              );
            })()
          ) : signupOpen ? (
            <HStack spacing="4px" color="#7c3aed" bg="rgba(124,58,237,0.1)" px="9px" py="3px" borderRadius="full" flexShrink={0}>
              <Icon as={FiUserPlus} boxSize="11px" />
              <Text fontSize="11px" fontWeight="700">Zapisy</Text>
            </HStack>
          ) : null}
        </HStack>

        {/* Panel treści w jednym zaokrąglonym tle: tytuł + terminy + pytanie + frekwencja */}
        <Flex direction="column" gap="9px" bg="rgba(67,0,152,0.04)" borderRadius="14px" p="13px 14px">
          <Heading fontSize="17.5px" fontWeight="800" lineHeight="1.3" color="primary" noOfLines={2}>
            {title || "Bez tytułu"}
          </Heading>
          {hasContentBox && (
            <>
            {terms && (
              <HStack spacing="6px" color="gray.600" fontSize="12px" fontWeight="600">
                <Icon as={FiCalendar} boxSize="12px" color="primary" flexShrink={0} />
                <Text noOfLines={1}>{terms}</Text>
              </HStack>
            )}
            {questionPreview?.content && (
              <Box minW={0}>
                <Text fontSize="13px" fontWeight="600" color="gray.700" noOfLines={1}>
                  „{htmlToPlainText(questionPreview.content)}”
                </Text>
                {options.length > 0 && (
                  <Text fontSize="11.5px" color="gray.500" noOfLines={1} mt="2px">
                    {options.slice(0, 3).map(o => htmlToPlainText(o)).join(" · ")}
                    {questionPreview.more > 0 ? ` · +${questionPreview.more}` : ""}
                  </Text>
                )}
              </Box>
            )}

            {showTurnout ? (
              <HStack spacing="9px">
                <Box flex="1" h="6px" bg="rgba(67,0,152,0.10)" borderRadius="full" overflow="hidden">
                  <Box h="100%" w={`${pct}%`} minW={votes > 0 ? "5px" : "0"} bgGradient="linear(to-r, #7b2ff7, #430098)" borderRadius="full" />
                </Box>
                <Text fontSize="11px" color="gray.500" fontWeight="600" flexShrink={0} whiteSpace="nowrap">
                  <Box as="span" color="primary" fontWeight="800">{pct}%</Box> · {votes}/{voters}
                  {qText ? ` · ${qText}` : ""}
                </Text>
              </HStack>
            ) : (
              (voters > 0 || qText) && (
                <Text fontSize="11.5px" color="gray.500" fontWeight="500">
                  {[voters > 0 ? `${voters} uprawnionych` : null, qText].filter(Boolean).join(" · ")}
                </Text>
              )
            )}
            </>
          )}
        </Flex>

        <Spacer />

        {/* Stopka: status po lewej, kompaktowa akcja po prawej */}
        <HStack justify="space-between" align="center" spacing="8px" pt="13px" borderTop="1px solid rgba(67,0,152,0.07)">
          <HStack spacing="5px" bg={meta.bg} color={meta.color} px="10px" py="4px" borderRadius="full" flexShrink={0}>
            <Icon as={meta.icon} boxSize="12px" />
            <Text fontSize="11.5px" fontWeight="700">{meta.label}</Text>
          </HStack>
          <HStack spacing="7px" flexShrink={0}>
          {protocolUrl && (
            <IconButton
              as="a"
              href={`${backendUrl}${protocolUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              aria-label="Pobierz protokół"
              icon={<FiDownload />}
              size="sm"
              variant="outline"
              borderColor="rgba(67,0,152,0.25)"
              color="primary"
              borderRadius="11px"
              _hover={{ bg: "rgba(67,0,152,0.06)" }}
            />
          )}
          <HStack spacing="6px" bg={ctaStyle.bg} color={ctaStyle.color} fontWeight="800" fontSize="13.5px" px="16px" py="9px" borderRadius="11px" boxShadow={ctaStyle.shadow} transition="all 0.15s" _groupHover={{ bg: ctaStyle.hover, transform: "translateY(-1px)" }}>
            <Text>{cta}</Text>
            <Icon as={FiArrowRight} boxSize="15px" />
          </HStack>
          </HStack>
        </HStack>
      </Flex>
    </Flex>
  );
};

export default VotingTile;
